// @ts-nocheck
// CoverCropper — ferramenta de corte de imagem. Proporção e dimensões de saída
// são parametrizáveis via props (aspect/outW/outH); defaults 2:3 1080×1620 (capa
// pôster), também usado como banner 4:3 1440×1080. Grade-guia (rule of thirds),
// arrastar pra posicionar e zoom. Exporta a imagem cortada como File pra upload.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '../icons';

export const CoverCropper: React.FC<{
  file: File | null;
  onCancel: () => void;
  onConfirm: (croppedFile: File) => void;
  aspect?: number;   // largura/altura (2/3 poster | 4/3 banner)
  outW?: number;
  outH?: number;
  title?: string;
  hint?: string;
}> = ({ file, onCancel, onConfirm, aspect = 2 / 3, outW = 1080, outH = 1620, title = 'Ajustar capa', hint }) => {
  const ASPECT = aspect, OUT_W = outW, OUT_H = outH;
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);       // 1..3 (relativo ao "cover")
  const [tx, setTx] = useState(0);             // px na tela
  const [ty, setTy] = useState(0);
  const [busy, setBusy] = useState(false);
  const frameRef = useRef<any>(null);
  const drag = useRef<any>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 }); // tamanho do frame na tela

  // Carrega a imagem
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { setImg(im); setScale(1); setTx(0); setTy(0); setFalhou(false); };
    // SEM `onerror` A TELA FICAVA MUDA PARA SEMPRE.
    //
    // O WebView do Android nao decodifica todo formato que a galeria oferece —
    // HEIC, o padrao da camera do iPhone, chega por WhatsApp o tempo todo e nao
    // abre num `Image()` ali. Sem este ramo, `img` ficava nulo, a tela mostrava
    // so o fundo preto com a grade, e o botao "Usar" continuava com a cor de
    // ativo enquanto `disabled` o travava. Nada explicava nada.
    im.onerror = () => { setImg(null); setFalhou(true); };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Mede o frame 2:3 disponível
  useEffect(() => {
    const measure = () => {
      const maxW = Math.min(window.innerWidth - 48, 360);
      const maxH = window.innerHeight - 230;
      let w = maxW, h = w / ASPECT;
      if (h > maxH) { h = maxH; w = h * ASPECT; }
      setFrame({ w: Math.round(w), h: Math.round(h) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ASPECT]);

  // "cover": escala base que cobre o frame
  const cover = img && frame.w ? Math.max(frame.w / img.naturalWidth, frame.h / img.naturalHeight) : 1;
  const s = cover * scale;
  const imgW = img ? img.naturalWidth * s : 0;
  const imgH = img ? img.naturalHeight * s : 0;

  // Clampa a translação pra nunca mostrar borda vazia
  const clamp = useCallback((nx: number, ny: number) => {
    const maxX = Math.max(0, (imgW - frame.w) / 2);
    const maxY = Math.max(0, (imgH - frame.h) / 2);
    return { x: Math.max(-maxX, Math.min(maxX, nx)), y: Math.max(-maxY, Math.min(maxY, ny)) };
  }, [imgW, imgH, frame.w, frame.h]);

  useEffect(() => { const c = clamp(tx, ty); if (c.x !== tx || c.y !== ty) { setTx(c.x); setTy(c.y); } }, [scale, clamp]); // eslint-disable-line

  // Drag (pointer)
  const onDown = (e: any) => {
    const pt = e.touches ? e.touches[0] : e;
    drag.current = { sx: pt.clientX, sy: pt.clientY, tx, ty };
  };
  const onMove = (e: any) => {
    if (!drag.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const c = clamp(drag.current.tx + (pt.clientX - drag.current.sx), drag.current.ty + (pt.clientY - drag.current.sy));
    setTx(c.x); setTy(c.y);
  };
  const onUp = () => { drag.current = null; };

  const confirm = async () => {
    if (!img) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUT_W; canvas.height = OUT_H;
      const ctx = canvas.getContext('2d');
      const k = OUT_W / frame.w; // frame px -> canvas px
      ctx.fillStyle = '#161210'; ctx.fillRect(0, 0, OUT_W, OUT_H);
      ctx.save();
      ctx.translate(OUT_W / 2 + tx * k, OUT_H / 2 + ty * k);
      ctx.scale(s * k, s * k);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();
      const blob: Blob = await new Promise((resolve, reject) => canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob retornou null')), 'image/jpeg', 0.9));
      if (!blob || !blob.size) throw new Error('blob de capa vazio');
      const cropped = new File([blob], 'capa.jpg', { type: 'image/jpeg' });
      onConfirm(cropped);
    } catch (e) {
      console.error('[cropper]', e);
      onCancel();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(8,6,5,0.92)', display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'calc(14px + env(safe-area-inset-top,0px)) 18px 10px', color: '#fff' }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: 15, fontWeight: 500 }}>Cancelar</button>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        {/* O botao so parecia desligado quando `busy` — com `img` nulo ele
            ficava com a cor de ativo e nao respondia ao toque. Botao aceso que
            nao obedece e pior que botao apagado. */}
        <button onClick={confirm} disabled={busy || !img} style={{ background: 'none', border: 'none', color: 'var(--ember, #E87A3B)', fontSize: 15, fontWeight: 700, opacity: (busy || !img) ? 0.45 : 1 }}>{busy ? 'Salvando…' : 'Usar'}</button>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <div ref={frameRef} style={{ position: 'relative', width: frame.w, height: frame.h, borderRadius: 10, overflow: 'hidden', touchAction: 'none', cursor: 'grab', boxShadow: '0 0 0 9999px rgba(8,6,5,0.55)' }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}>
          {falhou && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8, padding: 20, textAlign: 'center',
            }}>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Nao consegui abrir essa imagem</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.5 }}>
                Alguns formatos de foto (como os do iPhone) nao abrem aqui. Escolha outra, ou tire uma foto na hora.
              </div>
            </div>
          )}
          {img && (
            <img src={img.src} alt="" draggable={false} style={{
              position: 'absolute', left: '50%', top: '50%', width: imgW, height: imgH,
              transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`, userSelect: 'none', pointerEvents: 'none',
            }} />
          )}
          {/* grade-guia rule of thirds */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {[1, 2].map((i) => <div key={'v' + i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${(i * 100) / 3}%`, width: 1, background: 'rgba(255,255,255,0.35)' }} />)}
            {[1, 2].map((i) => <div key={'h' + i} style={{ position: 'absolute', left: 0, right: 0, top: `${(i * 100) / 3}%`, height: 1, background: 'rgba(255,255,255,0.35)' }} />)}
            <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(255,255,255,0.6)', borderRadius: 10 }} />
          </div>
        </div>
      </div>

      {/* zoom + dica */}
      <div style={{ padding: '14px 24px calc(20px + env(safe-area-inset-bottom,0px))', color: '#fff' }}>
        <div style={{ textAlign: 'center', fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginBottom: 14 }}>{hint || 'Arraste pra posicionar · arraste o zoom abaixo'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="search" size={16} color="rgba(255,255,255,0.6)" />
          <input type="range" min={1} max={3} step={0.01} value={scale} onChange={(e) => setScale(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--ember, #E87A3B)' }} />
        </div>
      </div>
    </div>
  );
};
